import { useEffect, useState } from "react";
import { LifeBuoy, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, fmtDateTime } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const CATEGORIES = ["shipment", "payment", "kyc", "warehouse", "other"];

export default function Tickets() {
  const [tickets, setTickets] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "shipment", subject: "", description: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => appApi.get("tickets").then((d) => setTickets(d.tickets || [])).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await appApi.post("tickets", form);
      toast.success("Ticket raised — our team replies fast.");
      setForm({ category: "shipment", subject: "", description: "" });
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="tickets-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Support</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Tickets</h1>
        </div>
        <button onClick={() => setShowForm((s) => !s)} data-testid="ticket-new-toggle"
          className="inline-flex items-center gap-2 bg-acid text-white text-sm font-bold px-5 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150">
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showForm ? "Cancel" : "New Ticket"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="ticket-form" className="border-2 border-obsidian bg-white p-6 space-y-4 max-w-2xl">
          {error && <div data-testid="ticket-error" className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{error}</div>}
          <div>
            <label htmlFor="ticket-category" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Category</label>
            <select id="ticket-category" data-testid="ticket-category-select" value={form.category} onChange={set("category")}
              className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="ticket-subject" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Subject</label>
            <input id="ticket-subject" data-testid="ticket-subject-input" required value={form.subject} onChange={set("subject")} placeholder="Short summary"
              className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          </div>
          <div>
            <label htmlFor="ticket-description" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Details</label>
            <textarea id="ticket-description" data-testid="ticket-description-input" required rows={4} value={form.description} onChange={set("description")}
              placeholder="AWB, order id, what happened…"
              className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          </div>
          <button type="submit" data-testid="ticket-submit" disabled={saving}
            className="bg-obsidian text-sky-300 font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(10,10,10,0.35)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
            {saving ? "Raising…" : "Raise Ticket"}
          </button>
        </form>
      )}

      <div className="space-y-3" data-testid="tickets-list">
        {(tickets || []).map((t) => (
          <div key={t.id} className="border-2 border-obsidian bg-white p-5 hover:shadow-[6px_6px_0px_0px_#0A0A0A] transition-all duration-150" data-testid={`ticket-card-${t.reference_no}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-obsidian text-sky-300 flex items-center justify-center"><LifeBuoy className="w-5 h-5" /></span>
                <div>
                  <p className="font-bold text-sm">{t.subject}</p>
                  <p className="text-xs font-mono text-zinc-500 mt-0.5">{t.reference_no} · {t.category?.toUpperCase()} · {fmtDateTime(t.created_at)}</p>
                </div>
              </div>
              <StatusBadge status={t.status} />
            </div>
            <p className="text-sm text-zinc-600 mt-3 whitespace-pre-line">{t.description}</p>
          </div>
        ))}
        {tickets && tickets.length === 0 && <p className="text-sm text-zinc-500" data-testid="tickets-empty">No tickets — everything running smooth.</p>}
        {!tickets && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading tickets…</p>}
      </div>
    </div>
  );
}
